// Der Feld-Katalog — das gemeinsame Vokabular aller Anzeigestellen des Portals.
//
// Er beantwortet zwei Fragen: WELCHE Angaben gibt es, und was steckt fachlich
// worin. Er beantwortet ausdrücklich NICHT, wie eine Angabe dargestellt wird —
// das steht je Tabelle in jobTableColumns.ts.
//
// Nur STAMMDATEN. Ablesedaten (OBIS, Ablesedatum, Ablesewert, Ablesefoto,
// Zählwerk-Kommentar) und Vermerke stehen nicht hier: ein Ablesewert ist das
// Ergebnis der Arbeit, kein Merkmal des Zählers. Für Ablesefoto und
// Zählwerk-Kommentar gibt es zudem showReadingImagesAllowed und
// showReadingCommentAllowed.
export interface FieldCatalogEntry {
    /** Config-Key der Angabe. Wird in features.fieldVisibility.globalHidden genannt. */
    key: string;
    /** i18n-Schlüssel des Labels — für die Doku und für die Config-Oberfläche (#892). */
    labelKey: string;
    /**
     * FACHLICHE Zugehörigkeit: „die Hausnummer gehört zur Straße". Steuert die
     * Kaskade — wer den Träger versteckt, versteckt alles darin.
     *
     * Bewusst NICHT `partOf` genannt, obwohl die Tabellen-Registry ein Feld
     * dieses Namens hat: die zwei tun Verschiedenes. `partOf` sagt, ob eine
     * Angabe in IHRER TABELLE eine eigene Spalte hat oder in einer Zelle
     * mitläuft — dieselbe Angabe ist in jobSelection Bestandteil der
     * Straßen-Spalte, in sharedJobs Bestandteil der Adress-Zelle und in
     * JobDetails Bestandteil einer Textzeile. Ein globaler Katalog kann das
     * nicht führen. Zwei Namen für zwei Begriffe, damit niemand sie verwechselt.
     */
    belongsTo?: string;
}

// Reihenfolge = die des Katalogs in der Spec. Sie ist keine Anzeigereihenfolge:
// die Reihenfolge in den Tabellen steht in jobTableColumns.ts, die in den
// Detailansichten in der jeweiligen Komponente.
export const FIELD_CATALOG: FieldCatalogEntry[] = [
    { key: 'job.status', labelKey: 'meterReadingJob.status' },
    { key: 'job.isShared', labelKey: 'management.accounts.share.tableColumns.isShared' },
    { key: 'jobConstraints.latestExecution', labelKey: 'meterReadingJob.jobConstraints.latestExecution' },

    { key: 'meter.meterNumber', labelKey: 'meterReadingJob.meterNumber' },
    { key: 'meter.meterType', labelKey: 'meterReadingJob.meterType' },
    { key: 'meter.maLoId', labelKey: 'meterReadingJob.maLoId' },

    { key: 'customer.customerNumber', labelKey: 'meterReadingJob.customer.customernumber' },
    { key: 'customer.summary', labelKey: 'meterReadingJob.customer.summary' },
    { key: 'customer.company', labelKey: 'meterReadingJob.customer.company', belongsTo: 'customer.summary' },
    { key: 'customer.name', labelKey: 'meterReadingJob.customer.name', belongsTo: 'customer.summary' },
    { key: 'customer.lastname', labelKey: 'meterReadingJob.customer.lastname', belongsTo: 'customer.name' },
    { key: 'customer.firstname', labelKey: 'meterReadingJob.customer.firstname', belongsTo: 'customer.name' },

    { key: 'partner.identifier', labelKey: 'meterReadingJob.partner.identifier' },
    { key: 'partner.name', labelKey: 'meterReadingJob.partner.name' },
    { key: 'partner.company', labelKey: 'meterReadingJob.partner.company', belongsTo: 'partner.name' },
    { key: 'partner.lastname', labelKey: 'meterReadingJob.partner.lastname', belongsTo: 'partner.name' },
    { key: 'partner.firstname', labelKey: 'meterReadingJob.partner.firstname', belongsTo: 'partner.name' },

    { key: 'meter.meterAddress.full', labelKey: 'meterReadingJob.address.fullAddress' },
    {
        key: 'meter.meterAddress.name',
        labelKey: 'meterReadingJob.address.name',
        belongsTo: 'meter.meterAddress.full',
    },
    {
        key: 'meter.meterAddress.company',
        labelKey: 'meterReadingJob.address.company',
        belongsTo: 'meter.meterAddress.name',
    },
    {
        key: 'meter.meterAddress.lastname',
        labelKey: 'meterReadingJob.address.lastname',
        belongsTo: 'meter.meterAddress.name',
    },
    {
        key: 'meter.meterAddress.firstname',
        labelKey: 'meterReadingJob.address.firstname',
        belongsTo: 'meter.meterAddress.name',
    },
    {
        key: 'meter.meterAddress.street',
        labelKey: 'meterReadingJob.address.street',
        belongsTo: 'meter.meterAddress.full',
    },
    // Es gibt bewusst KEINEN Key für „nur den Straßennamen": „Hauptstraße 12"
    // ohne „Hauptstraße" wäre eine Nummer ohne Bezug.
    {
        key: 'meter.meterAddress.houseNumber',
        labelKey: 'meterReadingJob.address.houseNumber',
        belongsTo: 'meter.meterAddress.street',
    },
    { key: 'meter.meterAddress.zip', labelKey: 'meterReadingJob.address.zip', belongsTo: 'meter.meterAddress.full' },
    { key: 'meter.meterAddress.city', labelKey: 'meterReadingJob.address.city', belongsTo: 'meter.meterAddress.full' },
    {
        key: 'meter.meterAddress.cityDistrict',
        labelKey: 'meterReadingJob.address.cityDistrict',
        belongsTo: 'meter.meterAddress.full',
    },

    // Diese drei heißen meter.meterAddress.*, gehören aber NICHT zur Anschrift —
    // ihr belongsTo ist leer. Wer die Anschrift ausblendet, behält sie. Das
    // Präfix stammt aus dem Auftragsmodell (jobs.types.ts:36-37), wo sie unter
    // meterAddress liegen; fachlich ist die Anschrift eine Adresse, der
    // Objektschlüssel eine Kennung des Objekts und der Hinweis eine Bemerkung
    // zur Ablesung. Fachliche Zugehörigkeit folgt nicht dem Feldpfad — genau
    // deshalb steht sie ausdrücklich in diesem Katalog.
    { key: 'meter.meterAddress.objectKey', labelKey: 'meterReadingJob.objectKey' },
    { key: 'meter.meterAddress.locationDescription', labelKey: 'meterReadingJob.address.locationDescription' },
    { key: 'meter.meterAddress.hint', labelKey: 'meterReadingJob.address.hint' },
];

export const FIELD_CATALOG_BY_KEY: Map<string, FieldCatalogEntry> = new Map(
    FIELD_CATALOG.map((entry) => [entry.key, entry]),
);

export const FIELD_CATALOG_KEYS: Set<string> = new Set(FIELD_CATALOG.map((entry) => entry.key));
